import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, finalize, skip } from 'rxjs';
import {
  SINGLE_VARIABLE_LIST_COLUMNS_SORT,
  SINGLE_VARIABLE_LIST_VIEW_DETAILS_ACTION,
  SINGLE_VARIABLE_LIST_FILTER_TABS,
} from './single-variable-list.constants';
import { Variable } from '../../variables/variable.model';
import { TableComponent } from '../../../../../ui-components/lib/table/table.component';
import { PaginatorComponent } from '../../../../../ui-components/lib/paginator/paginator.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { SearchFieldComponent } from '../../../../../ui-components/lib/search-field/search-field.component';
import {
  initialSearchTerm,
  SearchService,
} from '../../../../../ui-components/lib/search-field/search.service';
import { ActionListComponent } from '../../../../../core/common/components/action-list/action-list.component';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../common/constants/pagination.constants';
import { ADMIN_ROUTES } from '../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { VariableService } from '../../variables/variable.service';
import { ActionList } from '../../../../../core/common/models/action-list.model';
import { SearchEnum } from '../../../../common/enums/search.enum';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import {
  injectFloatingLayout,
  injectSystemTabsLayout,
} from '../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';

@Component({
  selector: 'lib-single-variable-list',
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    TableComponent,
    PaginatorComponent,
    BadgeComponent,
    SearchFieldComponent,
    ActionListComponent,
  ],
  templateUrl: './single-variable-list.component.html',
  styleUrl: './single-variable-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SingleVariableListComponent implements OnInit {
  /** Follows the opted-in parent page (e.g. Users Variables), which wraps this list in a white card. */
  protected readonly floating = injectFloatingLayout();
  /** Segmented tabs sit above the card, so the header starts the card. */
  protected readonly systemTabs = injectSystemTabsLayout();
  private readonly featureFlagsService = inject(FeatureFlagsService);
  /** Tables_new_design moves the search from the header into the table toolbar. */
  protected readonly searchInToolbar = computed(
    () =>
      this.floating() &&
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.TABLE.ui_enhancement
      )
  );
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  variableModule = input.required<string>();
  variableType = input.required<string>();

  Router = inject(Router);
  translocoService = inject(TranslocoService);
  variableService = inject(VariableService);
  tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchService = inject(SearchService);

  readonly searchInputWidth = SearchEnum.searchInputWidth;
  searchTerm = signal(initialSearchTerm());
  isMoreThanThreeCharacters = signal(
    this.searchService.isMinLength(this.searchTerm())
  );
  private readonly searchTerm$ = toObservable(this.searchTerm);

  variableList = signal<Variable[]>([]);
  isLoading = signal<boolean>(true);
  totalItems = signal<number>(0);
  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(20);

  defaultSort = signal<{ key: string; value: 'asc' | 'desc' }>({
    key: '',
    value: 'asc',
  });
  activeSort = signal<{ key: string; value: 'asc' | 'desc' }>(
    this.defaultSort()
  );

  viewDetailsAction = signal<ActionList>({
    ...SINGLE_VARIABLE_LIST_VIEW_DETAILS_ACTION,
    label: this.translocoService.translate(
      SINGLE_VARIABLE_LIST_VIEW_DETAILS_ACTION.label
    ),
  });

  columns = signal<TableColumns[]>(
    SINGLE_VARIABLE_LIST_COLUMNS_SORT.map(col => ({
      ...col,
      header: col.header ? this.translocoService.translate(col.header) : '',
      viewIconRouterLink: col.showViewIcon
        ? (row: any) => [
            this.tenantService.adminPath(
              `${this.variableModule()}/${ADMIN_ROUTES.SINGLE_VARIABLES}/${row.id}`
            ),
          ]
        : undefined,
    }))
  );

  handleSortColumn(column: TableColumns) {
    if (column.isSorted) {
      this.activeSort.set({
        key: column.sortKey || '',
        value: column.isSorted,
      });
    } else {
      this.activeSort.set(this.defaultSort());
    }
    this.currentPage.set(1);
    this.getVariableList();
  }

  tableFilterTabs = signal<TableFilterTab[]>(
    SINGLE_VARIABLE_LIST_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.translocoService.translate(tab.label),
    }))
  );

  ngOnInit(): void {
    this.getVariableList();
    this.searchTermWrapper();
  }

  searchTermWrapper() {
    this.searchTerm$
      .pipe(
        skip(1),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(term => {
        this.handleSearchTerm(term);
      });
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);
    if (
      this.searchService.applySearchTerm(term, this.isMoreThanThreeCharacters)
    ) {
      this.getVariableList();
    }
  }

  getVariableList() {
    this.isLoading.set(true);
    this.variableService
      .getVariables(
        this.currentPage(),
        this.currentSize(),
        this.variableModule(),
        this.variableType(),
        this.searchService.checkSearchLength(this.searchTerm()),
        this.activeSort()
      )
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(res => {
        this.variableList.set(res.data);
        this.totalItems.set(res.meta.total);
        this.totalPages.set(res.meta.last_page);
      });
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getVariableList();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getVariableList();
  }

  getViewActionList(id: number): ActionList[] {
    this.viewDetailsAction().routerLinks = [
      this.tenantService.adminPath(
        `${this.variableModule()}/${ADMIN_ROUTES.SINGLE_VARIABLES}/${id.toString()}`
      ),
    ];
    return [this.viewDetailsAction()];
  }

  actionListItemClick(action: string, row: Variable) {
    if (action === 'view') {
      this.Router.navigate([
        this.tenantService.adminPath(
          `${this.variableModule()}/${ADMIN_ROUTES.SINGLE_VARIABLES}/${row.id.toString()}`
        ),
      ]);
    }
  }
}
